import type { Id } from "@agroproj/backend/convex/_generated/dataModel";
import type {
  ProducerCreator,
  ProducerItem,
  ProducerPaginatedItem,
  ProducerSummary,
} from "@agroproj/backend/convex/types/producer";

type AddressView = {
  street?: string;
  number?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
};

export type ProducerView = {
  id: string;
  _id: Id<"producers">;
  name?: string | null;
  companyName?: string | null;
  tradingName?: string | null;
  documentType: "cpf" | "cnpj";
  documentValue: string;
  displayName: string;
  email?: string | null;
  phone1?: string | null;
  phone2?: string | null;
  status?: string | null;
  address: AddressView | null;
  primaryAddress?: unknown;
  document?: {
    type: "cpf" | "cnpj";
    value: string;
  };
  createdAt?: number;
  updatedAt?: number;
  createdBy?: Id<"users">;
  updatedBy?: Id<"users"> | null;
  organizationId?: Id<"organizations">;
  summary?: ProducerSummary;
  createdByUser?: ProducerCreator | null;
};

type RawProducer = ProducerItem | ProducerPaginatedItem;

function buildAddress(primaryAddress: unknown): AddressView | null {
  if (!primaryAddress) return null;
  const addr = primaryAddress as Record<string, unknown>;
  return {
    street: (addr.street as string | undefined) ?? undefined,
    number: (addr.number as string | undefined) ?? undefined,
    neighborhood: (addr.neighborhood as string | undefined) ?? undefined,
    city: (addr.city as string | undefined) ?? undefined,
    state: (addr.state as string | undefined) ?? undefined,
  } satisfies AddressView;
}

function computeDisplayName(producer: RawProducer): string {
  return (
    producer.name ||
    producer.companyName ||
    producer.tradingName ||
    producer.documentValue ||
    String(producer._id)
  );
}

export function normalizeProducer(producer: RawProducer): ProducerView {
  const producerRecord = producer as Record<string, unknown>;
  return {
    id: String(producer._id),
    _id: producer._id,
    name: producer.name ?? null,
    companyName: producer.companyName ?? null,
    tradingName: producer.tradingName ?? null,
    documentType: (producer.documentType as "cpf" | "cnpj") ?? "cpf",
    documentValue: producer.documentValue ?? "",
    displayName: computeDisplayName(producer),
    email: producer.email ?? null,
    phone1: (producerRecord.phone1 as string | null | undefined) ?? null,
    phone2: (producerRecord.phone2 as string | null | undefined) ?? null,
    status: producer.status ?? null,
    address: buildAddress(producerRecord.primaryAddress ?? null),
    primaryAddress: producerRecord.primaryAddress ?? undefined,
    document: {
      type: (producer.documentType as "cpf" | "cnpj") ?? "cpf",
      value: producer.documentValue ?? "",
    },
    createdAt: producer.createdAt ?? (producerRecord._creationTime as number | undefined),
    updatedAt:
      producer.updatedAt ??
      producer.createdAt ??
      (producerRecord._creationTime as number | undefined),
    createdBy: producer.createdBy ?? undefined,
    updatedBy:
      (producerRecord.updatedBy as Id<"users"> | null | undefined) ?? producer.createdBy ?? null,
    organizationId: producer.organizationId ?? undefined,
    summary: "summary" in producer ? producer.summary : undefined,
    createdByUser: (producerRecord.createdByUser as ProducerCreator | null | undefined) ?? null,
  } satisfies ProducerView;
}

export function normalizeProducers(producers: RawProducer[]): ProducerView[] {
  return producers.map((producer) => normalizeProducer(producer));
}
